import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInitialLoadingScreenManager } from '../../../hooks/useInitialLoadingScreenManager';
import { ChevronLeft, Plus, Trash2, Upload } from 'lucide-react';
import { Loader } from '../../../components/ui';
import { useDropzone } from 'react-dropzone';
import type { Topic } from '../../../types/topic';
import { useViewport } from '../../../contexts/ViewportContext';
import { useImageProcessing } from './useImageProcessing';
import { DropZone } from './DropZone';
import { ImageGrid } from './ImageGrid';
import { UploadStatus } from './UploadStatus';
import { ColumnSelector } from './ColumnSelector';
import { logger } from '../../../utils/logger';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';
import { useTopicSubscription } from '../../../hooks/useTopicSubscription';
import { moodboardKeys } from '../../../hooks/queries/moodboardKeys';
import {
  useAddMoodboardItems,
  useDeleteMoodboardItem,
  useDeleteMoodboardItems,
  useMoodboardItems,
} from '../../../hooks/queries/useMoodboard';

/**
 * NOTE: Offline mode support was planned but not implemented.
 * When adding offline support, consider using useAppConnectionState
 * for isOnline status to show offline indicators or disable uploads.
 */

interface MoodboardTopicProps {
  topic: Topic;
  hasCollaborators?: boolean;
  onBackClick: () => void;
}

export function MoodboardTopic({ topic, onBackClick }: MoodboardTopicProps) {
  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();
  const { markRouteComplete } = useInitialLoadingScreenManager();
  const [columns, setColumns] = useState(4);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use React Query hooks for data fetching and mutations
  const { data: itemsData, isLoading } = useMoodboardItems(topic.id);

  const addItemsMutation = useAddMoodboardItems();
  const deleteItemMutation = useDeleteMoodboardItem();
  const deleteItemsMutation = useDeleteMoodboardItems();

  // Memoize items array to prevent infinite re-renders
  const items = useMemo(() => itemsData || [], [itemsData]);

  // Set up real-time subscription for moodboard items
  // The hook handles subscription setup/cleanup; return value not needed
  const subscriptions = useMemo(
    () => [{ table: 'moodboard_items' as const, queryKey: moodboardKeys.byTopic(topic.id) }],
    [topic.id]
  );

  useTopicSubscription(topic.id, subscriptions);

  const handleImagesProcessed = useCallback(
    async (newImages: any[]) => {
      try {
        // Add the new images using the mutation
        await addItemsMutation.mutateAsync({
          topicId: topic.id,
          items: newImages,
        });
      } catch (error) {
        logger.error('Failed to add moodboard items', error);
      }
    },
    [topic.id, addItemsMutation]
  );

  const { isUploading, error, uploadStats, processImages, setUploadStats } = useImageProcessing({
    topicId: topic.id,
    onImagesProcessed: handleImagesProcessed,
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: processImages,
    accept: {
      'image/*': [],
    },
    disabled: isUploading || addItemsMutation.isPending,
    noClick: true,
    noKeyboard: true,
  });

  const handleDelete = async (imageId: string) => {
    try {
      // Use the mutation hook to delete the item
      await deleteItemMutation.mutateAsync({
        topicId: topic.id,
        itemId: imageId,
      });

      // Remove from selection if selected
      if (selectedImages.has(imageId)) {
        const newSelection = new Set(selectedImages);
        newSelection.delete(imageId);
        setSelectedImages(newSelection);
      }
    } catch (err) {
      console.error('Error deleting image:', err);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      const selectedImageIds = Array.from(selectedImages);

      // Use the mutation hook to delete multiple items
      await deleteItemsMutation.mutateAsync({
        topicId: topic.id,
        itemIds: selectedImageIds,
      });

      setSelectedImages(new Set());
    } catch (err) {
      console.error('Error deleting selected images:', err);
    }
  };

  const handleSelectImage = (imageId: string) => {
    const newSelection = new Set(selectedImages);
    if (newSelection.has(imageId)) {
      newSelection.delete(imageId);
    } else {
      newSelection.add(imageId);
    }
    setSelectedImages(newSelection);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processImages(files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Only mark as complete when loading is done
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [isLoading]); // Remove markRouteComplete from deps to prevent infinite updates

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader color="text-blue-500" className="mb-4" />
        </div>
      </div>
    );
  }

  // The items coming from React Query are already formatted
  const images = items;

  return (
    <div className="flex flex-col h-full">
      {/* Mobile header */}
      <div
        className="lg:hidden flex items-center h-14 px-4"
        style={{
          paddingTop: isMobile && isNativeApp ? `${statusBarHeight}px` : '0',
          height: isMobile && isNativeApp ? `calc(3.5rem + ${statusBarHeight}px)` : '3.5rem',
        }}
      >
        <div className="flex-none">
          <button onClick={onBackClick} className="p-2 -ml-2 text-gray-600 hover:text-gray-900">
            <ChevronLeft size={24} />
          </button>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2">
          <span className="text-base font-semibold text-gray-900 whitespace-nowrap">
            {topic.name}
          </span>
        </div>

        <div className="flex-none w-10" />
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        <DropZone {...getRootProps()} isDragActive={isDragActive} isEmpty={images.length === 0}>
          <input {...getInputProps()} />

          <div className="p-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {images.length > 0 && (
              <ImageGrid
                images={images}
                isMobile={isMobile}
                columns={columns}
                onDelete={handleDelete}
                selectedImages={selectedImages}
                onSelectImage={handleSelectImage}
              />
            )}
          </div>
        </DropZone>
      </div>

      {/* Fixed bottom controls */}
      <div className="sticky bottom-0 w-full">
        {/* Desktop toolbar */}
        {!isMobile && images.length > 0 && (
          <div className="p-4 flex items-center justify-between">
            {/* Left side - Delete selected button */}
            <div className="w-[200px]">
              {selectedImages.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="inline-flex items-center whitespace-nowrap gap-2 px-4 py-2 text-red-600 hover:text-red-700 transition-colors"
                >
                  <Trash2 size={20} />
                  <span>Delete Selected ({selectedImages.size})</span>
                </button>
              )}
            </div>

            {/* Centered column selector */}
            <ColumnSelector columns={columns} onChange={setColumns} />

            {/* Upload button on the right */}
            <div className="w-[200px] flex justify-end">
              <button
                onClick={handleUploadClick}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 text-base text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Upload size={20} />
                <span>Upload images</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload status */}
      <UploadStatus
        totalFiles={uploadStats.total}
        completedFiles={uploadStats.completed}
        failedFiles={uploadStats.failed}
        isUploading={isUploading}
        onClose={() => setUploadStats({ total: 0, completed: 0, failed: 0 })}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Mobile floating buttons */}
      {isMobile && (
        <>
          {/* Delete selected button */}
          {selectedImages.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="fixed bottom-7 left-6 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-red-600 transition-colors inline-flex items-center whitespace-nowrap gap-2"
            >
              <Trash2 size={20} />
              <span>Delete Selected ({selectedImages.size})</span>
            </button>
          )}

          {/* Add button */}
          <button
            onClick={handleUploadClick}
            disabled={isUploading}
            className="fixed bottom-6 right-6 w-14 h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-[0_4px_10px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.25)] transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={24} />
          </button>
        </>
      )}
    </div>
  );
}
